import { useState } from 'react';
import { ChildPracticeMetadata } from '../../../../types';
import { Button, Menu, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { amalgamAllResults, amalgamResultsByPractice, medicalAllResults, medicalResultsByPractice, pharmaceuticalAllResults, pharmaceuticalResultsByPractice } from '../Reports';
import { useAppDispatch } from '../../../../redux/store';
import { addError } from '../../../../redux/alerts';

type AllowedCategories = 'amalgam' | 'mwaste' | 'pharma';

interface ExportButtonProps {
  category: AllowedCategories;
  childPractices: ChildPracticeMetadata[];
  customerNumber: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({ category, childPractices, customerNumber }) => {
  const dispatch = useAppDispatch();
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportOpenEl, setExportOpenEl] = useState<HTMLElement | null>(null);
  const exportOpen = Boolean(exportOpenEl);

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportOpenEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportOpenEl(null);
  };

  const handleExportByType = async (type: string) => {
    handleExportClose();
    setExportLoading(true);

    try {
      if (category === 'amalgam') {
        if (type === 'allResults') {
          await amalgamAllResults(childPractices);
        } else if (type === 'resultsByPractice') {
          amalgamResultsByPractice(childPractices, customerNumber);
        }
      } else if (category === 'mwaste') {
        if (type === 'allResults') {
          await medicalAllResults(childPractices);
        } else if (type === 'resultsByPractice') {
          medicalResultsByPractice(childPractices, customerNumber);
        }
      } else if (category === 'pharma') {
        if (type === 'allResults') {
          await pharmaceuticalAllResults(childPractices);
        } else if (type === 'resultsByPractice') {
          pharmaceuticalResultsByPractice(childPractices, customerNumber);
        }
      }

      setExportLoading(false);
    } catch (error: any) {
      setExportLoading(false);
      const message = error?.response?.data?.message || error?.message || 'Failed to load report';
      dispatch(addError(message));
    }
  };

  return (
    <>
      <Button aria-haspopup="true" variant="contained" name="export" onClick={handleExportClick} disabled={exportLoading}>
        {exportLoading ? 'Loading...' : 'Export'}
        <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" />
      </Button>
      <Menu
        id="account-menu"
        anchorEl={exportOpenEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        open={exportOpen}
        onClose={handleExportClose}
        MenuListProps={{
          'aria-labelledby': 'export-button'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem onClick={() => handleExportByType('allResults')}>All Results</MenuItem>
        <MenuItem onClick={() => handleExportByType('resultsByPractice')}>Results by Practice</MenuItem>
      </Menu>
    </>
  );
};

export default ExportButton;
